// ============================================================
// The weapon's template on the map, and who it would catch
// ============================================================
// THE SAME FUNCTIONS THE ATTACK USES, deliberately. The blast ring was a
// bare 2D distance filter once: no line of sight, no horde bulk, and the
// thrower counted among the casualties. That was harmless while nothing
// consumed the ring - and the moment it decides damage, a preview that
// disagrees with the shot is worse than no preview at all. Pure;
// `useAreaPreviews` memoizes each on its inputs.

import { combatantsInCircle, combatantsInCone, combatantsAlongBeam, arcChain } from '../../engine/battlefield/area';
import { spawnersOf, selectSpawned, spawnedOutlines } from '../../engine/battlefield/spawnedArea';
import { DEFAULT_BEAM_WIDTH_M } from '../../engine/areaShapes';
import { bearingBetween } from '../../engine/battlefield/facing';
import { overwatchOf } from '../../engine/overwatch';
import { EMPTY } from './panelDefaults';

/** The shape's sizes read off the expected area (v8.372.5). */
function templateFromArea(area, weaponRangeM) {
  const reaches = area.shape === 'cone' || area.shape === 'beam';
  const beam = area.shape === 'beam';
  return {
    blastRadius: area.shape === 'blast' ? area.radius : 0,
    sprayDegrees: area.shape === 'cone' ? area.angle : 0,
    beamActive: beam,
    templateReach: reaches && area.length > 0 ? area.length : weaponRangeM,
    beamHalfWidth: (beam && area.width > 0 ? area.width : DEFAULT_BEAM_WIDTH_M) / 2,
  };
}

/**
 * The template to draw: the selected weapon's expected `area` when there
 * is one, otherwise the older callers' four separate props.
 */
export function templateOf({ area, blastRadius, sprayDegrees, beamActive, weaponRangeM }) {
  if (area) return templateFromArea(area, weaponRangeM);
  return {
    blastRadius,
    sprayDegrees,
    beamActive,
    templateReach: weaponRangeM,
    beamHalfWidth: DEFAULT_BEAM_WIDTH_M / 2,
  };
}

/**
 * The options every catch shares: sight through the terrain (or none when
 * terrain is switched off map-wide), hidden solids for the GM, and horde
 * bulk as the panel resolved it once (`useAreaPreviews`).
 */
function sightOptions({ ignoreTerrain, solids, isGm, hordeMagnitude }) {
  return { solids: ignoreTerrain ? EMPTY : solids, includeHidden: isGm, hordeMagnitude };
}

/** The actor never catches themselves. */
const excluding = (actorId) => (actorId ? [actorId] : undefined);

/**
 * Blast is drawn where the measure line ends: you point at a spot and the
 * template asks who is standing in it. Failing that, whoever you are
 * aiming at - the same fallback the attack itself uses, so the preview is
 * the shot.
 */
export function blastCentreOf(blastRadius, line, targetId, tokens) {
  if (!(blastRadius > 0)) return null;
  if (line?.to) return { x: line.to.x, z: line.to.z, elevation: line.to.token?.elevation ?? 0 };
  const aimed = targetId ? tokens[targetId] : null;
  return aimed ? { x: aimed.x, z: aimed.z, elevation: aimed.elevation } : null;
}

/** Who the blast would catch. */
export function caughtInBlast(centre, radius, tokenList, sight) {
  if (!centre) return EMPTY;
  return combatantsInCircle(centre, radius, tokenList, {
    ...sightOptions(sight),
    exclude: excluding(sight.actorId),
    eligible: sight.eligible,
  });
}

/**
 * A template laid from the shooter toward where the tape ends, or toward
 * the target when there is no tape. Null with nothing to aim at.
 */
export function aimFrom(shooter, line, targetId, tokens, reach) {
  const at = line?.to || (targetId ? tokens[targetId] : null);
  if (!at) return null;
  return { apex: shooter, bearing: bearingBetween(shooter, at), length: Math.max(1, reach) };
}

/** Who the flamer's cone would catch - the thrower included, as the attack has it. */
export function caughtInCone(aim, degrees, tokenList, sight) {
  if (!aim) return EMPTY;
  return combatantsInCone(aim.apex, aim.bearing, aim.length, degrees, tokenList, {
    ...sightOptions(sight),
    eligible: sight.eligible,
  });
}

/** Who the beam's lance of ground would catch (v8.292.0). */
export function caughtAlongBeam(aim, halfWidth, tokenList, sight) {
  if (!aim) return EMPTY;
  const options = { ...sightOptions(sight), exclude: excluding(sight.actorId), eligible: sight.eligible };
  return combatantsAlongBeam(aim.apex, aim.bearing, aim.length, tokenList, options, halfWidth);
}

/**
 * The arc (v8.372.5): the chain the lightning would walk from the aimed
 * target, on the expected sizes - the same walk the attack makes.
 */
export function arcPreviewOf(area, primary, tokenList, sight) {
  if (area?.shape !== 'arc' || !(area.reach > 0) || !primary) return null;
  const chain = arcChain(primary, tokenList, area.reach, area.leaps, {
    ...sightOptions(sight),
    exclude: excluding(sight.actorId),
    eligible: sight.eligible,
    sightPerLeap: area.sightPerLeap,
  });
  return { primary, chain, ids: chain.slice(1).map((c) => c.id) };
}

/** Whoever the primary template is predicted to catch, as `{ id }`s. */
function primaryVictims(shape, caught) {
  if (shape === 'arc') return caught.arc?.chain ?? EMPTY;
  const ids = shape === 'blast' ? caught.blast : shape === 'cone' ? caught.cone : caught.beam;
  return ids.map((id) => ({ id }));
}

/**
 * THE SPAWNED TEMPLATE (v8.373.5): from whoever the primary is predicted
 * to catch, through the same two functions the shot calls. It assumes
 * nobody steps clear - an avoid test cannot be previewed.
 */
export function spawnPreviewOf(area, caught, { targetId, shooter, tokenList, tokens, sight, spawnEligible }) {
  const spawn = area?.spawn;
  if (!spawn) return null;
  const victims = primaryVictims(area.shape, caught);
  if (!victims.length) return null;
  const spawners = spawnersOf(victims, new Set(), targetId, spawn.spawnedBy);
  const attacker = shooter || null;
  const struck = [...victims.map((v) => v.id), ...(sight.actorId ? [sight.actorId] : [])];
  const found = selectSpawned(spawners, attacker, tokenList, spawn, {
    struck,
    ...sightOptions(sight),
    eligible: spawnEligible,
    sightPerLeap: spawn.sightPerLeap,
  });
  return {
    outlines: spawnedOutlines(spawners, attacker, tokenList, spawn),
    caughtAt: found.map((v) => tokens[v.id]).filter(Boolean),
  };
}

/**
 * DECLARED KILL ZONES (v8.308.0) - one wedge per combatant holding an
 * `Ov:` condition, drawn whether or not they are the actor. A zone nobody
 * can see is a zone nobody remembers, and the whole action is a promise
 * to the table about ground that is covered.
 *
 * A bare `Ov` parses to null and draws nothing, which is the honest
 * rendering of a watcher with no bearing.
 */
export function overwatchZonesOf(roster, tokens, weaponRangeM) {
  return (roster || [])
    .map((c) => ({ c, zone: overwatchOf(c.state?.conditions) }))
    .filter(({ c, zone }) => zone && tokens[c.id])
    .map(({ c, zone }) => ({
      id: c.id,
      apex: tokens[c.id],
      bearing: zone.heading,
      length: Math.max(1, weaponRangeM || 30),
    }));
}

/** Names for a list of ids, the id itself when the roster has none. */
export const namesOf = (ids, names) => ids.map((id) => names[id] || id);

/** The markers standing at a list of ids, skipping any not on the map. */
export const markersOf = (ids, tokens) => ids.map((id) => tokens[id]).filter(Boolean);
